import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DesignServicesOutlinedIcon from "@mui/icons-material/DesignServicesOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTranslation } from "react-i18next";
import {
  pagePrimaryButtonSx,
  pageFieldSx,
} from "../../styles/pageModernStyles";
import {
  bob,
  cloudLift,
  motionSafe,
  rise,
  studioDialogBackdropSx,
  studioDialogPaperSx,
} from "../AI/studioDialogMotion";

const ACCENT = "#00897b";
const ACCENT_MISSING = "#ef6c00";

function EditProjectStage({ accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 92,
        mt: 1.25,
        mb: 0.5,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.08),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "18%",
          top: 20,
          width: 46,
          height: 50,
          borderRadius: 1.35,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.4),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.14)}`,
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <DesignServicesOutlinedIcon sx={{ fontSize: 24, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: "44%",
          top: 28,
          width: 44,
          height: 44,
          borderRadius: 1.25,
          bgcolor: alpha(accent, 0.12),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.45),
          display: "grid",
          placeItems: "center",
          animation: `${bob} 2.5s ease-in-out infinite`,
          animationDelay: "0.12s",
          ...motionSafe,
        }}
      >
        <CalendarMonthOutlinedIcon sx={{ fontSize: 22, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: "14%",
          top: 14,
          width: 54,
          height: 54,
          borderRadius: 1.5,
          bgcolor: alpha(accent, 0.16),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.5),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 18px ${alpha(accent, 0.18)}`,
          animation: `${cloudLift} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <EditOutlinedIcon sx={{ fontSize: 28, color: accent }} />
      </Box>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: `${32 + i * 10}%`,
            bottom: 16,
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: alpha(accent, 0.35 + i * 0.15),
            animation: `${bob} ${1.8 + i * 0.2}s ease-in-out infinite`,
            animationDelay: `${i * 0.1}s`,
            ...motionSafe,
          }}
        />
      ))}
    </Box>
  );
}

/**
 * Dialog Compás: editar nombre / temporada del proyecto.
 * Misma familia visual que Nuevo proyecto / Eliminar (blur + rise + stage).
 */
export default function SkillAgentEditProjectDialog({
  open,
  onClose,
  projectName = "",
  nameValue = "",
  onNameChange,
  seasonValue = "",
  onSeasonChange,
  seasonChips = [],
  missingSeason = false,
  saving = false,
  error = "",
  onClearError,
  onSubmit,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = missingSeason ? ACCENT_MISSING : ACCENT;
  const nameHint = String(projectName || "").trim();

  const fieldSx = {
    ...pageFieldSx,
    "& .MuiOutlinedInput-root": {
      bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#000", 0.02),
    },
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={180}
      slotProps={{
        backdrop: { sx: studioDialogBackdropSx },
      }}
      BackdropProps={{ sx: studioDialogBackdropSx }}
      PaperProps={{
        sx: {
          ...studioDialogPaperSx,
          animation: open ? `${rise} 280ms ease both` : undefined,
          ...motionSafe,
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha(
            accent,
            0.02,
          )} 55%)`,
          animation: `${rise} 300ms ease both`,
          ...motionSafe,
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: accent, fontWeight: 800, letterSpacing: 0.06 }}
        >
          {t("skillAgent.dialog.editProjectEyebrow", {
            defaultValue: "Compás · Proyecto",
          })}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          {t("skillAgent.dialog.editProjectTitle", {
            defaultValue: "Editar proyecto",
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {missingSeason
            ? t("skillAgent.dialog.editProjectLeadMissingSeason", {
                defaultValue:
                  "Este proyecto está en Sin temporada. Elige una (ej. AW2027) para recolocararlo.",
              })
            : t("skillAgent.dialog.editProjectLead", {
                defaultValue:
                  "Cambia el nombre o la temporada (ej. AW2027, SS28).",
              })}
          {nameHint ? (
            <>
              {" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, color: "text.primary" }}
              >
                «{nameHint}»
              </Box>
            </>
          ) : null}
        </Typography>
        <EditProjectStage accent={accent} />
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={1.5}>
          {error ? (
            <Alert severity="error" onClose={onClearError}>
              {error}
            </Alert>
          ) : null}
          <TextField
            autoFocus={!missingSeason}
            fullWidth
            size="small"
            label={t("skillAgent.dialog.projectNameLabel", {
              defaultValue: "Nombre",
            })}
            value={nameValue}
            onChange={(e) => onNameChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !saving) {
                e.preventDefault();
                onSubmit?.();
              }
            }}
            disabled={saving}
            sx={fieldSx}
          />
          <TextField
            autoFocus={missingSeason}
            fullWidth
            size="small"
            label={t("skillAgent.dialog.seasonLabel", {
              defaultValue: "Temporada",
            })}
            placeholder="AW2027"
            value={seasonValue}
            onChange={(e) => onSeasonChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !saving) {
                e.preventDefault();
                onSubmit?.();
              }
            }}
            disabled={saving}
            helperText={
              missingSeason
                ? t("skillAgent.dialog.seasonRequiredHint", {
                    defaultValue:
                      "Sin temporada queda al final del árbol. Así lo recolocas.",
                  })
                : undefined
            }
            sx={{
              ...fieldSx,
              ...(missingSeason
                ? {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: alpha(accent, 0.55),
                    },
                  }
                : null),
            }}
          />
          {seasonChips.length ? (
            <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
              {seasonChips.slice(0, 12).map((s) => {
                const selected =
                  String(seasonValue || "")
                    .trim()
                    .toUpperCase() === String(s).toUpperCase();
                return (
                  <Chip
                    key={s}
                    size="small"
                    label={s}
                    onClick={() => onSeasonChange?.(s)}
                    sx={{
                      fontWeight: 700,
                      bgcolor: selected ? alpha(accent, 0.18) : undefined,
                      borderColor: selected ? accent : undefined,
                      color: selected ? accent : undefined,
                      border: "1px solid",
                      transition: "transform 0.12s ease",
                      "&:hover": { transform: "translateY(-1px)" },
                    }}
                    variant={selected ? "filled" : "outlined"}
                  />
                );
              })}
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ textTransform: "none" }}
        >
          {t("skillAgent.common.cancel", { defaultValue: "Cancelar" })}
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={saving || !String(nameValue || "").trim()}
          sx={{
            ...pagePrimaryButtonSx,
            textTransform: "none",
            fontWeight: 700,
            bgcolor: accent,
            "&:hover": { bgcolor: alpha(accent, 0.88) },
            minWidth: 96,
          }}
        >
          {saving ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            t("skillAgent.common.save", { defaultValue: "Guardar" })
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
